import { usePropertyServicePropertyCoefficientsList } from '@insurance/api/queries'
import { PropertyCoefficientGet } from '@insurance/api/requests'
import { useValidationsField } from '@insurance/hooks'
import { Button, Col, Flex, Form, Input, Row, Select } from 'antd'
import { Add, Trash } from 'iconsax-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { usePropertyInsuranceValues } from './PropertyInsuranceContext'
import { formProps } from './utils'

import type { FormListFieldData } from 'antd'
import type { RuleObject } from 'antd/es/form'

type CoefficientRowProps = {
  field: FormListFieldData
  fieldKey: number
  coefficients?: { coefficient?: number }[]
  coefficientData?: PropertyCoefficientGet[]
  isLoading: boolean
  disabled?: boolean
  onRemove: (name: number) => void
}

const CoefficientRow = ({
  field,
  fieldKey,
  coefficients,
  coefficientData,
  isLoading,
  disabled,
  onRemove
}: CoefficientRowProps) => {
  const { t } = useTranslation()
  const { required } = useValidationsField()
  const {
    ItemCoefficientsProps,
    ItemCoefficientProps,
    ItemCoefficientValueProps,
    itemsKey
  } = formProps(t)

  const currentCoefficient = Form.useWatch([
    itemsKey.name,
    fieldKey,
    ItemCoefficientsProps.name,
    field.name,
    ItemCoefficientProps.name
  ])

  const selectedCoefficients = useMemo(() => {
    const selected = new Set<number>()
    const coefficientsArray = coefficients || []
    coefficientsArray.forEach(
      (coef: { coefficient?: number }, index: number) => {
        if (
          index !== field.name &&
          coef?.coefficient !== undefined &&
          coef?.coefficient !== null
        ) {
          selected.add(coef.coefficient)
        }
      }
    )
    return selected
  }, [coefficients, field.name])

  const coefficientOptions = useMemo(() => {
    if (!coefficientData) return []
    return coefficientData
      .filter(
        (item) =>
          item.id !== undefined &&
          (!selectedCoefficients.has(item.id) || item.id === currentCoefficient)
      )
      .map((item) => ({
        label: item.name || '',
        value: item.id
      }))
  }, [coefficientData, selectedCoefficients, currentCoefficient])

  const selectedCoefficientData = useMemo(() => {
    if (!currentCoefficient || !coefficientData) return null
    return coefficientData.find((item) => item.id === currentCoefficient)
  }, [currentCoefficient, coefficientData])

  const valueValidationRules = useMemo(() => {
    const rules: Array<{
      required?: boolean
      message?: string
      validator?: (rule: RuleObject, value: string) => Promise<void>
    }> = [required]

    if (
      selectedCoefficientData?.min !== undefined &&
      selectedCoefficientData?.max !== undefined
    ) {
      const min = parseFloat(selectedCoefficientData.min)
      const max = parseFloat(selectedCoefficientData.max)

      if (!isNaN(min) && !isNaN(max)) {
        rules.push({
          validator: (_: RuleObject, value: string) => {
            if (!value) {
              return Promise.resolve()
            }

            const numValue = parseFloat(value)
            if (isNaN(numValue)) {
              return Promise.reject(new Error(t('this_field_is_required')))
            }

            if (numValue < min || numValue > max) {
              return Promise.reject(
                new Error(
                  `${t('coefficient_value_range')}: ${
                    selectedCoefficientData.min
                  } - ${selectedCoefficientData.max}`
                )
              )
            }

            return Promise.resolve()
          }
        })
      }
    }

    return rules
  }, [selectedCoefficientData, required, t])

  return (
    <Row key={field.key} gutter={[12, 12]} align="middle">
      <Col span={12}>
        <Form.Item
          {...ItemCoefficientProps}
          name={[field.name, ItemCoefficientProps.name]}
          rules={[required]}
        >
          <Select
            placeholder={ItemCoefficientProps.label}
            options={coefficientOptions}
            loading={isLoading}
            disabled={disabled}
            showSearch
          />
        </Form.Item>
      </Col>

      <Col span={10}>
        <Form.Item
          {...ItemCoefficientValueProps}
          name={[field.name, ItemCoefficientValueProps.name]}
          rules={valueValidationRules}
          dependencies={[
            itemsKey.name,
            fieldKey,
            ItemCoefficientsProps.name,
            field.name,
            ItemCoefficientProps.name
          ]}
        >
          <Input
            type="number"
            step="any"
            placeholder={ItemCoefficientValueProps.label}
          />
        </Form.Item>
      </Col>

      <Col span={2} style={{ marginTop: 6 }}>
        <Button
          type="primary"
          danger
          icon={<Trash size={16} />}
          disabled={disabled}
          onClick={() => onRemove(field.name)}
        />
      </Col>
    </Row>
  )
}

type Props = {
  fieldKey: number
}

export const CoefficientsFields = ({ fieldKey }: Props) => {
  const { t } = useTranslation()
  const { ItemCoefficientsProps, itemsKey } = formProps(t)
  const { isEditable } = usePropertyInsuranceValues()

  const { data, isLoading } = usePropertyServicePropertyCoefficientsList({
    ordering: 'name'
  })

  const coefficients = Form.useWatch([
    itemsKey.name,
    fieldKey,
    ItemCoefficientsProps.name
  ])

  return (
    <Form.List name={[fieldKey, ItemCoefficientsProps.name]}>
      {(fields, { add, remove }) => (
        <Flex vertical gap={12}>
          {fields.map((field) => (
            <CoefficientRow
              key={field.key}
              field={field}
              fieldKey={fieldKey}
              coefficients={coefficients}
              coefficientData={data}
              isLoading={isLoading}
              disabled={isEditable}
              onRemove={remove}
            />
          ))}

          <Button
            type="dashed"
            onClick={() => add()}
            icon={<Add size={16} />}
            block
            disabled={isEditable}
          >
            {t('add_coefficient')}
          </Button>
        </Flex>
      )}
    </Form.List>
  )
}
